/**
 * Algorand API Service
 *
 * Provides access to Algorand account data via the public Algonode API.
 * No API key required - completely public.
 *
 * Documentation: https://algonode.io/api/
 */

import { logger } from './logger';
import { callCoinGeckoAPI } from './api';
import type {
  AlgorandAccount,
  AlgorandTransaction,
  AlgorandAsset,
  AlgorandWalletData,
  AlgorandAssetWithInfo,
  AlgorandAccountResponse,
  AlgorandTransactionsResponse,
  AlgorandAssetResponse,
} from '../types/algorand.types';

// Algonode public API (mainnet)
const ALGONODE_API_URL = 'https://mainnet-api.algonode.cloud';
const ALGONODE_INDEXER_URL = 'https://mainnet-idx.algonode.cloud';

// microAlgos to ALGO conversion (1 ALGO = 1,000,000 microAlgos)
const MICROALGOS_TO_ALGO = 1_000_000;

// Request timeout in ms
const REQUEST_TIMEOUT_MS = 15000;

// Rate limiting
const REQUEST_DELAY_MS = 100;

// Cache for asset info
const assetInfoCache = new Map<number, AlgorandAsset>();

/**
 * Delay helper for rate limiting
 */
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Fetch with timeout
 */
const fetchWithTimeout = async (url: string): Promise<Response> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    const response = await fetch(url, {
      signal: controller.signal,
      headers: {
        'Accept': 'application/json',
      }
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
};

/**
 * Convert microAlgos to ALGO
 */
export const microAlgosToAlgo = (microAlgos: number): number => {
  return microAlgos / MICROALGOS_TO_ALGO;
};

/**
 * Validate Algorand address format (58 characters, alphanumeric)
 */
export const isValidAlgorandAddress = (address: string): boolean => {
  return /^[A-Z2-7]{58}$/.test(address);
};

/**
 * Algorand API Service
 */
export const algorandAPI = {
  /**
   * Get account information
   */
  async getAccount(address: string): Promise<AlgorandAccount | null> {
    try {
      if (!isValidAlgorandAddress(address)) {
        logger.warn('AlgorandAPI', `Invalid address format: ${address}`);
        return null;
      }

      const response = await fetchWithTimeout(`${ALGONODE_API_URL}/v2/accounts/${address}`);

      if (!response.ok) {
        if (response.status === 404) {
          logger.warn('AlgorandAPI', `Account not found: ${address}`);
          return null;
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data: AlgorandAccountResponse = await response.json();
      return data as unknown as AlgorandAccount;
    } catch (error) {
      logger.error('AlgorandAPI', `Failed to fetch account ${address}`, error);
      throw error;
    }
  },

  /**
   * Get account transactions
   */
  async getTransactions(
    address: string,
    limit: number = 50
  ): Promise<AlgorandTransaction[]> {
    try {
      const response = await fetchWithTimeout(
        `${ALGONODE_INDEXER_URL}/v2/accounts/${address}/transactions?limit=${limit}`
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data: AlgorandTransactionsResponse = await response.json();
      return data.transactions || [];
    } catch (error) {
      logger.error('AlgorandAPI', `Failed to fetch transactions for ${address}`, error);
      return [];
    }
  },

  /**
   * Get asset information
   */
  async getAssetInfo(assetId: number): Promise<AlgorandAsset | null> {
    // Check cache first
    if (assetInfoCache.has(assetId)) {
      return assetInfoCache.get(assetId)!;
    }

    try {
      const response = await fetchWithTimeout(
        `${ALGONODE_INDEXER_URL}/v2/assets/${assetId}`
      );

      if (!response.ok) {
        return null;
      }

      const data: AlgorandAssetResponse = await response.json();
      const asset = data.asset;

      // Cache the result
      assetInfoCache.set(assetId, asset);

      return asset;
    } catch (error) {
      logger.error('AlgorandAPI', `Failed to fetch asset info for ${assetId}`, error);
      return null;
    }
  },

  /**
   * Get ALGO price from CoinGecko
   */
  async getAlgoPrice(): Promise<number> {
    try {
      const data = await callCoinGeckoAPI('/simple/price', {
        ids: 'algorand',
        vs_currencies: 'usd'
      });
      return data['algorand']?.usd || 0;
    } catch (error) {
      logger.error('AlgorandAPI', 'Failed to fetch ALGO price', error);
      return 0;
    }
  },

  /**
   * Get complete wallet data for an address
   */
  async getWalletData(address: string, algoPrice: number): Promise<AlgorandWalletData | null> {
    try {
      // Fetch account info
      const account = await this.getAccount(address);
      if (!account) {
        return null;
      }

      await delay(REQUEST_DELAY_MS);

      // Fetch transactions
      const transactions = await this.getTransactions(address, 25);
      await delay(REQUEST_DELAY_MS);

      // Fetch asset info for each asset (with rate limiting)
      const assetsWithInfo: AlgorandAssetWithInfo[] = [];
      for (const holding of (account.assets || []).slice(0, 20)) {
        const assetInfo = await this.getAssetInfo(holding['asset-id']);
        if (assetInfo) {
          const decimals = assetInfo.params.decimals;
          assetsWithInfo.push({
            assetId: holding['asset-id'],
            name: assetInfo.params.name || 'Unknown',
            unitName: assetInfo.params['unit-name'] || '',
            balance: holding.amount / Math.pow(10, decimals),
            decimals,
            url: assetInfo.params.url,
          });
        }
        await delay(REQUEST_DELAY_MS);
      }

      // Calculate balances
      const balance = microAlgosToAlgo(account.amount);
      const pendingRewards = microAlgosToAlgo(account['pending-rewards']);
      const totalRewards = microAlgosToAlgo(account.rewards);

      return {
        address,
        balance,
        balanceUsd: balance * algoPrice,
        pendingRewards,
        pendingRewardsUsd: pendingRewards * algoPrice,
        totalRewards,
        status: account.status,
        isParticipating: account.status === 'Online',
        assets: assetsWithInfo,
        recentTransactions: transactions,
        lastUpdated: Date.now(),
      };
    } catch (error) {
      logger.error('AlgorandAPI', `Failed to get wallet data for ${address}`, error);
      return null;
    }
  },

  /**
   * Get aggregated data for multiple addresses
   */
  async getMultipleWalletsData(
    addresses: string[],
    algoPrice: number
  ): Promise<AlgorandWalletData[]> {
    const results: AlgorandWalletData[] = [];

    for (const address of addresses) {
      const walletData = await this.getWalletData(address, algoPrice);
      if (walletData) {
        results.push(walletData);
      }
      await delay(500);
    }

    return results;
  },

  /**
   * Get transaction type display name
   */
  getTransactionTypeName(type: string): string {
    const typeMap: Record<string, string> = {
      'pay': 'Payment',
      'keyreg': 'Key Registration',
      'acfg': 'Asset Config',
      'axfer': 'Asset Transfer',
      'afrz': 'Asset Freeze',
      'appl': 'Application Call',
      'stpf': 'State Proof',
    };

    return typeMap[type] || type;
  },

  /**
   * Format timestamp for display
   */
  formatTimestamp(roundTime: number): Date {
    return new Date(roundTime * 1000);
  }
};

export default algorandAPI;
